import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Enrollment } from '@spike/membership-model';
import { SidePanel, Spinner } from 'components/UI';
import { ClientPaymentMethod } from '@spike/payments-model';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import SelectableCreditCard from 'components/UI/V2/SelectableCreditCard/SelectableCreditCard';
import AdyenPayment, { CompletedInfo } from 'components/Payments/Adyen/AdyenPayment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import Button from 'components/UI/V2/Button/Button';
import { useDispatch, useSelector } from 'react-redux';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import {
    MembershipsStatus,
    retryMultiplePaymentsThunk,
    retryPaymentThunk,
    updateEnrollmentPaymentMethodThunk,
    updateMultipleEnrollmentsPaymentMethodThunk
} from '@spike/memberships-action';
import { RootState } from 'store';
import { getClientPaymentMethodsThunk, PaymentsStatus } from '@spike/payments-action';
import useNonInitialEffect from '@versiondos/hooks';

interface UpdateEnrollmentCardDrawerProps {
    onClose: () => void;
    enrollment: Enrollment | null;
    onDone?: () => void;
    onFinishUpdate?: () => void;
    onFinishUpdateAndPay?: () => void;
    customButtonLabel?: string;
    loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        containerCardsDrawer: {
            //'zIndex': 100000000001,
            'display': 'flex',
            'height': '100%',
            'flexDirection': 'column',
            'padding': '30px 30px 30px 35px',
            [theme.breakpoints.up('md')]: {
                zIndex: '1001 !important'
            },
            '&.MuiDrawer-root.MuiDrawer-modal': {},
            '& .MuiDrawer-paperAnchorRight': {
                width: '100%',
                [theme.breakpoints.up('md')]: {
                    width: '590px !important',
                    zIndex: 999
                }
            }
        },
        backdrop: {
            backgroundColor: 'transparent'
        },
        headerDrawer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '30px 0px 25px 0px'
        },
        titleDrawer: {
            fontSize: 22,
            fontWeight: 600,
            [theme.breakpoints.down('sm')]: {
                fontSize: 20
            }
        },
        iconDrawer: {
            fontSize: 24,
            fontWeight: 500,
            [theme.breakpoints.down('sm')]: {
                fontSize: 20
            }
        },
        bodyDrawer: {
            width: 'calc(100% - 2px)',
            display: 'block',
            margin: '0 auto',
            paddingTop: 1,
            paddingBottom: 1
        },
        cardNumber: {
            fontWeight: 500,
            paddingLeft: 10
        },
        defaultBadge: {
            fontSize: 14,
            lineHeight: 1.43,
            color: '#5E8677',
            borderRadius: 30,
            padding: '3.5px 11px',
            display: 'block',
            backgroundColor: '#E9F0ED'
        },
        primaryBadgeWrapper: {
            display: 'flex',
            width: '100%',
            marginTop: 5,
            [theme.breakpoints.down('md')]: {
                paddingBottom: 8
            }
        },
        primaryBadge: {
            backgroundColor: '#5E8677',
            color: '#FFF'
        },
        expired: {
            backgroundColor: '#EF4F57',
            color: '#fff',
            fontSize: 12,
            borderRadius: 30,
            padding: '4px 8px',
            fontWeight: 500,
            lineHeight: 1,
            marginLeft: 8,
            position: 'relative',
            top: -1,
            [theme.breakpoints.up('md')]: {
                fontSize: 14
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: 10,
                top: 1
            },
            [theme.breakpoints.down(375)]: {
                '& svg': {
                    display: 'none'
                }
            }
        },
        cardPrimaryInfoWrapper: {
            display: 'flex',
            flexDirection: 'column',
            marginRight: 12,
            [theme.breakpoints.up('md')]: {
                marginRight: 85
            },
            [theme.breakpoints.up('sm')]: {
                marginRight: 45
            }
        },
        cardNumberWrapper: {
            display: 'flex',
            alignItems: 'center',
            minWidth: 111,
            [theme.breakpoints.up('sm')]: {
                minWidth: 115
            }
        },
        cardExpirationWrapper: {
            display: 'flex',
            alignItems: 'flex-start'
        },
        cardExpirationText: {
            fontSize: 16,
            fontWeight: 500
        },
        checkboxCard: {
            marginBottom: 15,
            [theme.breakpoints.down('sm')]: {
                padding: '16px 12px'
            }
        },
        addCardText: {
            fontSize: 16,
            fontWeight: 600,
            color: '#92B4A7',
            cursor: 'pointer'
        },
        containerDrawer: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            paddingLeft: 24,
            paddingRight: 24,
            paddingBottom: 24,
            [theme.breakpoints.down('sm')]: {
                paddingLeft: 16,
                paddingRight: 16
            }
        },
        footerDrawer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 24,
            paddingBottom: 0,
            borderTop: '1px solid #D4D4D4',
            marginLeft: -34,
            marginRight: -34,
            [theme.breakpoints.down('sm')]: {
                paddingTop: 12,
                paddingBottom: 12
            }
        },
        footerDrawerButton: {
            width: 'calc(100% - 68px)',
            display: 'flex'
        },
        closeButton: {
            display: 'inline-block',
            cursor: 'pointer',
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        }
    })
);

const UpdateEnrollmentCardDrawer: React.FC<UpdateEnrollmentCardDrawerProps> = ({
    onClose,
    enrollment,
    onDone,
    customButtonLabel,
    onFinishUpdate,
    onFinishUpdateAndPay,
    loading
}) => {
    const classes = useStyles();
    const enrollmentCardId = enrollment?.paymentMethod?.id;
    const [error, setError] = useState<string | undefined>(undefined);
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const [cardSelected, setCardSelected] = useState<ClientPaymentMethod | null>(null);
    const unorderedCards = useSelector<RootState, ClientPaymentMethod[]>(state => state.payments.clientPaymentMethods);
    const cards = [...unorderedCards].sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1));
    const [loadingCards, setLoadingCards] = useState(true);
    const [processing, setProcessing] = useState(true);
    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const [showAddCard, setShowAddCard] = useState(false);

    useEffect(() => {
        if (enrollment) {
            setLoadingCards(true);
            fetchClientPaymentMethods();
        }
    }, [enrollment?.id]);

    //Remove this after testing
    //enrollment.status = 'past_due';

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GettingClientPaymentMethods:
                setLoadingCards(true);
                setProcessing(true);
                break;
            case PaymentsStatus.GetClientPaymentMethodsSuccess:
                setLoadingCards(false);
                setProcessing(false);
                break;
        }
    }, [paymentsStatus]);

    useNonInitialEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.UpdatePaymentMethodSuccess:
                if (enrollment && enrollment.status != 'past_due') {
                    setLoadingCards(false);
                    setProcessing(false);
                }
                break;
            case MembershipsStatus.UpdatingPaymentMethod:
                setLoadingCards(true);
                setCardSelected(null);
                break;
            case MembershipsStatus.UpdatePaymentMethodError:
                setLoadingCards(false);
                setProcessing(false);
                break;
            case MembershipsStatus.UpdatingMultiplePaymentMethods:
                setLoadingCards(true);
                setCardSelected(null);
                break;
            case MembershipsStatus.UpdateMultiplePaymentMethodsSuccess:
                if (enrollment && enrollment.status != 'past_due') {
                    setLoadingCards(false);
                    setProcessing(false);
                }
                break;
            case MembershipsStatus.UpdateMultiplePaymentMethodsError:
                setLoadingCards(false);
                setProcessing(false);
                break;
            case MembershipsStatus.RetryPaymentSuccess:
                onClose();
                setProcessing(false);
                break;
            case MembershipsStatus.RetryPaymentError:
                if (enrollment) {
                    dispatch(getClientPaymentMethodsThunk(apiClientWrapper, enrollment.pets[0].clientId));
                }
                setProcessing(false);
                break;
            case MembershipsStatus.RetryingPayment:
                setProcessing(true);
                break;
            case MembershipsStatus.RetryingMultiplePayments:
                setProcessing(true);
                break;
            case MembershipsStatus.RetryMultiplePaymentsSuccess:
                onClose();
                setProcessing(false);
                break;
            case MembershipsStatus.RetryMultiplePaymentsError:
                if (enrollment) {
                    dispatch(getClientPaymentMethodsThunk(apiClientWrapper, enrollment.pets[0].clientId));
                }
                setProcessing(false);
                break;
            default:
                break;
        }
    }, [membershipsStatus]);

    if (enrollment == null) {
        return (
            <SidePanel
                onClose={onClose}
                className={classes.containerCardsDrawer}
                side="right"
                classesInner={{
                    root: classes.containerCardsDrawer
                }}
                classesBackdrop={{
                    classes: {
                        root: classes.backdrop
                    }
                }}
                showCloseButton={!showAddCard}
            >
                <Box className={classes.containerDrawer}>
                    <Box className={classes.headerDrawer}>
                        <Typography className={classes.titleDrawer}>Select Credit Card</Typography>
                    </Box>

                    <Box className={classes.bodyDrawer}>
                        <Spinner />
                    </Box>
                </Box>
            </SidePanel>
        );
    }

    const isLoading = () => {
        return loading || loadingCards;
    };

    const renderTitle = () => {
        if (showAddCard)
            return (
                <Typography className={classes.titleDrawer}>
                    {' '}
                    <FontAwesomeIcon
                        cursor="pointer"
                        onClick={handleCloseAddCard}
                        icon={faArrowLeft}
                    />{' '}
                    Add New Card
                </Typography>
            );
        return <Typography className={classes.titleDrawer}>Select Credit Card</Typography>;
    };

    const completeHandler = (info: CompletedInfo) => {
        if (info.success) {
            setLoadingCards(true);
            setTimeout(() => {
                fetchClientPaymentMethods();
            }, 2500);
        } else {
            setError(info.message);
        }
        handleCloseAddCard();
    };

    const fetchClientPaymentMethods = () => {
        dispatch(getClientPaymentMethodsThunk(apiClientWrapper, enrollment.pets[0].clientId));
    };

    const cardSelectedHandler = (card: ClientPaymentMethod) => {
        //if (card.id !== cardSelected?.id) return;
        setCardSelected(card);
    };

    const showNotification = () => {
        if (enrollment.status === 'past_due') {
            return false;
        }
        return true;
    };

    const getEnrollmentIds = () => {
        return enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0
            ? enrollment.pastDueEnrollments.map(e => e.id).join(',')
            : enrollment.id.toString();
    };

    const asyncUpdateEnrollment = async () => {
        let enrollmentIds = getEnrollmentIds();
        //If the enrollment has past due enrollments, we need to update the current enrollment and the past due enrollments
        //And we should not process the payment for the current enrollment in the retry
        if (enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0) {
            enrollmentIds = enrollmentIds + ',' + enrollment.id;
        }
        await dispatch(
            //updateEnrollmentPaymentMethodThunk(apiClientWrapper, enrollment.id, cardSelected!.id, showNotification())
            updateMultipleEnrollmentsPaymentMethodThunk(
                apiClientWrapper,
                enrollmentIds,
                cardSelected!.id,
                showNotification()
            )
        );
        if (enrollment.status === 'past_due') {
            //await dispatch(retryPaymentThunk(apiClientWrapper, enrollment.id));
            await dispatch(retryMultiplePaymentsThunk(apiClientWrapper, enrollmentIds));
        }
        if (onFinishUpdate) {
            onFinishUpdate();
        }
    };

    const actionButtonHandler = () => {
        setProcessing(true);
        asyncUpdateEnrollment();
    };

    const handleClose = () => {
        onClose();
        setCardSelected(null);
        setLoadingCards(false);
    };

    const handleCloseAddCard = () => {
        setShowAddCard(false);
    };

    const renderButtonLabel = () => {
        if (customButtonLabel) return customButtonLabel;
        if (enrollment.status === 'active') {
            return 'Update';
        }
        if (enrollment.status === 'past_due') {
            return 'Update & Pay';
        }
        return 'Update';
    };

    const handleAddCard = () => {
        setShowAddCard(true);
    };

    const isButtonDisabled = () => {
        return processing || loadingCards || !cardSelected || cardSelected.id === enrollmentCardId;
    };

    const getCardSelected = (card: ClientPaymentMethod) => {
        if (processing) return false;
        if (cardSelected) {
            return cardSelected.id === card.id;
        }
        if (enrollmentCardId) {
            return enrollmentCardId === card.id;
        }
        return false;
    };

    const closeButton = (
        <div
            className={classes.closeButton}
            onClick={onClose}
        >
            <FontAwesomeIcon
                icon={faTimes}
                fontSize={24}
                color="#00000"
            />
        </div>
    );

    return (
        <SidePanel
            onClose={handleClose}
            className={classes.containerCardsDrawer}
            side="right"
            classesInner={{
                root: classes.containerCardsDrawer
            }}
            classesBackdrop={{
                classes: {
                    root: classes.backdrop
                }
            }}
            showCloseButton={!showAddCard}
        >
            <Box className={classes.containerDrawer}>
                <Box className={classes.headerDrawer}>
                    <Typography className={classes.titleDrawer}>{renderTitle()}</Typography>
                    {closeButton}
                </Box>

                <OverlayScrollbarsComponent style={{ height: '100%' }}>
                    <Box className={classes.bodyDrawer}>
                        {isLoading() && <Spinner />}
                        {cards.length > 0 &&
                            !isLoading() &&
                            !showAddCard &&
                            cards.map(card => (
                                <SelectableCreditCard
                                    key={card.id}
                                    card={card}
                                    selected={getCardSelected(card)}
                                    onSelect={cardSelectedHandler}
                                />
                            ))}
                        {showAddCard && (
                            <AdyenPayment
                                clientId={enrollment.pets[0].clientId}
                                onError={setError}
                                actionType="addPaymentMethod"
                                onComplete={completeHandler}
                                hideRemovePaymentMethodButton
                                hideStoredPaymentMethods
                            />
                        )}
                        {!showAddCard && !isLoading() && (
                            <Typography
                                onClick={handleAddCard}
                                className={classes.addCardText}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add New Card
                            </Typography>
                        )}
                    </Box>
                </OverlayScrollbarsComponent>

                {!showAddCard && (
                    <Box className={classes.footerDrawer}>
                        <Box className={classes.footerDrawerButton}>
                            <Button
                                label={renderButtonLabel()}
                                fullWidth={true}
                                variant="black"
                                size="large"
                                disabled={isButtonDisabled()}
                                isLoading={processing || loadingCards || loading}
                                onClick={actionButtonHandler}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </SidePanel>
    );
};

export default UpdateEnrollmentCardDrawer;
